import Button from '../shared/Button.js';
import React from 'react';
import {useAppLocation as useLocation, useAppNavigate as useNavigate} from '../app/navigation/index.js';
// eslint-disable-next-line no-unused-vars -- JSX-only use
import RecordCollaborationSection from '../shared/RecordCollaborationSection.jsx';
// eslint-disable-next-line no-unused-vars -- JSX-only use
import RecordSequenceNav from '../shared/RecordSequenceNav.tsx';
import {recordSeqNavOptions} from '../lib/recordSequence.js';
// eslint-disable-next-line no-unused-vars -- JSX-only use
import InlineNotice from '../shared/InlineNotice.jsx';
import RecordPageLoadError from '../shared/RecordPageLoadError.jsx';
/* eslint-disable no-unused-vars -- shell primitives are used in JSX only */
import {
  RecordPageFrame,
  RecordPageLoading,
  RecordPageNotFound,
  RecordPageBody,
  RecordBackLink,
  RecordTitle,
} from '../shared/RecordPageShell.jsx';
/* eslint-enable no-unused-vars */
import {recordControl, recordFieldLabel} from '../shared/recordPageControls.jsx';

import {processingStatusLabel} from '../lib/processingStatusDisplay.js';
import {navigateToProcessingRoute, processingSourceRoute} from '../lib/processingNav.js';
// eslint-disable-next-line no-unused-vars -- JSX-only use (eslint flat config has no react/jsx-uses-vars rule)
// eslint-disable-next-line no-unused-vars -- JSX-only use (eslint flat config has no react/jsx-uses-vars rule)
import Badge from '../shared/Badge.jsx';
import {detachCattleFromProcessingBatch} from '../lib/processingDetachApi.js';
import {unscheduleCattleProcessingBatch} from '../lib/processingBatchDeleteApi.js';
import {
  batchHasAllHangingWeights,
  batchMissingHangingTags,
  dateToMonthKey,
  projectPlannedRoster,
  validateRealBatchRename,
} from '../lib/cattleForecast.js';
import type {CattleProcessingBatchRow} from '../lib/cattleForecast.js';
import {forecastFromBundle, markBatchComplete, reopenBatch} from '../lib/cattleForecastApi.js';
import type {CattleForecastBundle} from '../lib/cattleForecastApi.js';
// eslint-disable-next-line no-unused-vars -- JSX-only use (eslint flat config has no react/jsx-uses-vars rule)
import ProjectedRosterTable from './ProjectedRosterTable.tsx';
import {todayCentralISO} from '../lib/dateUtils.js';
import {invalidateCattleWeighInsCache} from '../lib/cattleCache.js';
import {newCattleRequestId} from '../lib/cattleOperationsApi.js';
import {RESOURCE} from '../app/data/routeDataCatalog.js';
import type {CattleBatchDetailData, CattleForecastBundleData} from '../app/data/routeDataCatalog.js';
import {useRouteResource} from '../app/data/useRouteResource.js';
import {MUTATION} from '../app/data/routeMutationCatalog.js';
import type {CattleBatchPatchVars} from '../app/data/routeMutationCatalog.js';
import {useRouteMutation} from '../app/data/useRouteMutation.js';
import {reportSaveFailed, reportSaved, reportSaving} from '../lib/saveStatus.js';
import Header from '../shared/Header.tsx';
import {
  computeRuminantBatchTotals,
  RuminantBatchStat,
  RuminantBatchStatsGrid,
  ruminantBatchDetachButtonStyle,
  ruminantBatchRowCardStyle,
  ruminantBatchWeightLabelStyle,
} from './ruminantPresentation.js';

type CattleRecord = Record<string, any>;
type CattleBatchRecord = CattleProcessingBatchRow & CattleRecord;
type Notice = {kind?: 'error' | 'warning' | 'success' | 'info'; message?: string | null};
type CattleBatchPageProps = {sb: any; fmt: (...args: any[]) => any; authState: any};

// The catalog rethrows PostgREST's `error.message` unchanged, so reading
// `.message` off the rejection reproduces the notice text the `{error}` /
// `r.error` envelopes produced — including the "undefined" a message-less error
// would have rendered before. This is a conversion, not a rewording.
const writeErrorMessage = (err: unknown): string =>
  err && typeof err === 'object' ? String((err as {message?: unknown}).message) : String(err);

const CATTLE_FORECAST_BUNDLE_PARAMS = Object.freeze({});

function resolveProcessedDate(batch: CattleBatchRecord | null | undefined) {
  if (batch && batch.actual_process_date) return batch.actual_process_date;
  if (batch && batch.planned_process_date) return batch.planned_process_date;
  return todayCentralISO();
}

function ageAtProcessing(birthDate: any, processDate: any) {
  if (!birthDate || !processDate) return '—';
  const birthMs = new Date(String(birthDate) + 'T12:00:00Z').getTime();
  const processMs = new Date(String(processDate) + 'T12:00:00Z').getTime();
  if (!Number.isFinite(birthMs) || !Number.isFinite(processMs)) return '—';
  const days = Math.floor((processMs - birthMs) / 86400000);
  if (days < 0) return '—';
  const years = Math.floor(days / 365);
  const months = Math.floor((days % 365) / 30);
  return years > 0 ? years + 'y ' + months + 'm' : months + 'm';
}

export default function CattleBatchPage({sb, fmt, authState}: CattleBatchPageProps) {
  const navigate = useNavigate();
  const location: any = useLocation();

  // Migration 240: this page's client-side Activity emitters are gone. Every
  // write it issues — batch.update, batch.weight, batch.complete, batch.reopen
  // — is the audited operation, whose Activity row lands in the write's own
  // transaction; detach and unschedule were already server-side (081/100).
  const batchId = location.pathname.slice('/cattle/batches/'.length);
  // Originating list order handed through route state; absent on direct links.
  const recordSeq = location.state?.recordSeq || null;
  function navigateSeq(id: any) {
    navigate('/cattle/batches/' + id, recordSeqNavOptions(recordSeq));
  }

  // Persisted batch state is route data, keyed on batchId. No component-local
  // mirror means a late query for batch A cannot overwrite batch B after
  // previous/next navigation; the resource owns cancellation and key changes.
  const batchResource = useRouteResource(
    RESOURCE.CATTLE_BATCH_DETAIL,
    React.useMemo(() => ({batchId}), [batchId]),
  );
  const batchData = batchResource.data as CattleBatchDetailData | null;
  const batch = batchData ? (batchData.batch as CattleBatchRecord | null) : null;
  const cattle = batchData ? (batchData.cattle as CattleRecord[]) : [];
  const allBatches = batchData ? (batchData.allBatches as CattleBatchRecord[]) : [];
  const loading = batchResource.loading;
  const loadAll = batchResource.refetch;
  const batchError = batchResource.error as CattleRecord | null;
  const loadError: Notice | null = batchError
    ? {
        kind: 'error',
        message:
          'Could not load cattle processing batch. Please refresh the page. (' +
          (batchError.message || String(batchError)) +
          ')',
      }
    : null;

  // The bundle is shared with forecast-only detail. It stays disabled for
  // active/complete records, whose actual roster needs no broad forecast read.
  const forecastBundle = useRouteResource(RESOURCE.CATTLE_FORECAST_BUNDLE, CATTLE_FORECAST_BUNDLE_PARAMS, {
    enabled: batch?.status === 'scheduled',
  });
  const forecastBundleData = forecastBundle.data as CattleForecastBundleData | null;

  // The record page's one declared write, handed the `sb` this surface was
  // given rather than the module singleton, so the page keeps exactly the
  // client it has always written through.
  //
  // `patchBatch` serves the weight grid, the rename and the scheduled-date
  // editor. Since migration 240 its transport is the audited
  // `cattle_batch_operation` RPC (batch.weight / batch.update); the entry's
  // eight-resource invalidation set is unchanged, so splitting it into three
  // entries would still duplicate an identical set for no reader's benefit.
  const patchBatch = useRouteMutation<CattleBatchPatchVars, null>(MUTATION.CATTLE_BATCH_PATCH, {client: sb});

  const [notice, setNotice] = React.useState<Notice | null>(null);
  const [cowDraft, setCowDraft] = React.useState<Record<string, any>>({});
  const [renameDraft, setRenameDraft] = React.useState('');
  const [renameErr, setRenameErr] = React.useState<string | null>(null);
  const [scheduledDateDraft, setScheduledDateDraft] = React.useState('');
  const [unscheduling, setUnscheduling] = React.useState(false);

  const role = authState && authState.role;
  const canEdit = role === 'admin' || role === 'management';

  React.useEffect(() => {
    setNotice(null);
    setCowDraft({});
    setRenameDraft('');
    setRenameErr(null);
    setScheduledDateDraft('');
    setUnscheduling(false);
  }, [batchId]);

  React.useEffect(() => {
    setRenameDraft(batch?.name || '');
    setScheduledDateDraft(batch?.planned_process_date || '');
  }, [batch?.id, batch?.name, batch?.planned_process_date]);

  // Projected roster for SCHEDULED batches — cows_detail is empty until
  // Send-to-Processor, so the cohort renders from the scoped canonical bundle
  // (cattle.forecast.bundle → forecastFromBundle → projectPlannedRoster;
  // the same source data as the Planned list and the Processing Drawer).
  // Fail-closed: a failed forecast load renders an
  // explicit unavailable state, never zero/fabricated weights. Re-runs when
  // the planned date changes so a moved batch recomputes its cohort through
  // the same math.
  const projectedRoster = React.useMemo<CattleRecord | null>(() => {
    if (!batch || batch.status !== 'scheduled') return null;
    if (forecastBundle.loading) return {state: 'loading'};
    if (forecastBundle.error || !forecastBundleData) {
      const error = forecastBundle.error as CattleRecord | null;
      return {state: 'unavailable', reason: (error && error.message) || 'load_failed'};
    }
    // Preserve the old helper's server-snapshot checks. The keyed record may
    // be a moment behind this concurrently fetched bundle; never construct a
    // scheduled projection from a batch that the bundle says was removed or
    // advanced to another lifecycle state.
    const bundleBatch = (forecastBundleData.batches as CattleBatchRecord[]).find(
      (entry) => entry && entry.id === batch.id,
    );
    if (!bundleBatch) return {state: 'unavailable', reason: 'batch_not_found'};
    if (bundleBatch.status !== 'scheduled') return {state: 'unavailable', reason: 'not_scheduled'};
    const monthKey = dateToMonthKey(bundleBatch.planned_process_date);
    if (!monthKey) return {state: 'unavailable', reason: 'no_planned_date'};
    const forecast = forecastFromBundle(forecastBundleData as unknown as CattleForecastBundle);
    const roster = projectPlannedRoster(forecast, monthKey);
    return roster.ok ? {state: 'ok', roster} : {state: 'unavailable', reason: roster.reason};
  }, [batch, forecastBundle.error, forecastBundle.loading, forecastBundleData]);

  React.useEffect(() => {
    if (!loading && location.hash) {
      const anchor = location.hash.slice(1);
      setTimeout(() => {
        const el = document.getElementById(anchor);
        if (el) el.scrollIntoView({behavior: 'smooth', block: 'center'});
      }, 200);
    }
  }, [loading, location.hash]);

  function cowsDetail() {
    return Array.isArray(batch?.cows_detail) ? batch.cows_detail : [];
  }
  // (recomputeTotals is gone with migration 240: the operation recomputes both
  // totals from the locked array server-side — the attach body's exact
  // semantics, proved against the client formula on the scratch harness.)

  async function saveCowWeight(cattleId: string, field: string, value: any) {
    if (!canEdit || !batch) return;
    setNotice(null);
    // Migration 240: the cell edit is ONE element's ONE field through the
    // audited operation — the page no longer builds the whole cows_detail
    // array or the totals. `expected` is the cell's value as this page read
    // it; a concurrent attach/detach serializes behind the batch row lock and
    // survives (the whole-array write this replaced dropped it — the defect
    // the lane exists to fix, reproduced on the scratch harness).
    const cell = cowsDetail().find((r) => r && r.cattle_id === cattleId);
    // A local one-cell patch of THIS closure's snapshot, for the auto-complete
    // check below only — the write itself sends one field, and the server owns
    // the array and both totals. The closure's `batch` is stale after the
    // await (the invalidation refetch lands in new props, not here), which is
    // exactly why the original code checked a locally-built snapshot too.
    const patched = cowsDetail().map((r) =>
      r.cattle_id === cattleId ? {...r, [field]: value === '' || value == null ? null : parseFloat(value)} : r,
    );
    const saveKey = `cattle-batch-weight:${batch.id}:${cattleId}:${field}`;
    const saveAttempt = reportSaving(saveKey);
    try {
      await patchBatch.run({
        batchId: batch.id,
        operation: 'batch.weight',
        target: {cattle_id: cattleId},
        expected: {[field]: cell && cell[field] != null ? cell[field] : null},
        change: {[field]: value === '' || value == null ? null : parseFloat(value)},
        requestId: newCattleRequestId(),
      });
      reportSaved(saveKey, saveAttempt);
    } catch (err) {
      reportSaveFailed(saveKey, () => void saveCowWeight(cattleId, field, value), saveAttempt);
      setNotice({kind: 'error', message: 'Weight save failed: ' + writeErrorMessage(err)});
      return;
    }
    const nextBatch: CattleBatchRecord = {...batch, cows_detail: patched};
    if (field === 'hanging_weight' && nextBatch.status === 'active' && batchHasAllHangingWeights(nextBatch)) {
      const processedDate = resolveProcessedDate(nextBatch);
      try {
        // Migration 240: markBatchComplete is the audited batch.complete
        // operation now (its status.changed Activity row is server-written).
        await markBatchComplete(sb, batch.id, {processedDate});
      } catch (e) {
        const error = e as CattleRecord;
        setNotice({kind: 'error', message: 'Auto-complete failed: ' + (error.message || String(e))});
      }
    }
    // THIS `await loadAll()` STAYS, and the sheep pilot's three did not. The
    // difference is real, not an oversight.
    //
    // `patchBatch.run` awaits the declared invalidation set, which covers the
    // weight write above completely. It does NOT cover the auto-complete's
    // batch.complete, which runs between the two and is a SECOND write on this
    // row — a second operation with its own request id, not declared here, and
    // only reached on the last hanging weight. Removing this line would leave
    // that write with no refresh at all on the surface that issued it.
    //
    // The cost is one extra refetch of cattle.batch.detail on the common path,
    // where the previous code did one. Declaring complete in the catalog is
    // the change that would make this line removable; until then, an extra
    // fetch is the correct trade against a stale status badge.
    await loadAll();
  }

  async function handleMarkComplete() {
    if (!canEdit || !batch) return;
    setNotice(null);
    if (!batchHasAllHangingWeights(batch)) {
      const missing = batchMissingHangingTags(batch);
      setNotice({
        kind: 'error',
        message:
          'Cannot mark complete — these tags are missing hanging weights:\n\n#' +
          missing.join('  #') +
          "\n\nEnter every cow's hanging weight first.",
      });
      return;
    }
    const processedDate = resolveProcessedDate(batch);
    try {
      // Migration 240: the audited batch.complete operation (server-written
      // status.changed Activity; the hanging-weight rule is enforced
      // server-side too, mirroring the client check above).
      await markBatchComplete(sb, batch.id, {processedDate});
      await loadAll();
    } catch (e) {
      const error = e as CattleRecord;
      setNotice({kind: 'error', message: 'Mark complete failed: ' + (error.message || String(e))});
    }
  }

  async function handleReopen() {
    if (!canEdit || !batch) return;
    setNotice(null);
    try {
      // Migration 240: batch.reopen keeps cows_detail AND actual_process_date
      // server-side, exactly as the client rule did.
      await reopenBatch(sb, batch.id);
      await loadAll();
    } catch (e) {
      const error = e as CattleRecord;
      setNotice({kind: 'error', message: 'Reopen failed: ' + (error.message || String(e))});
    }
  }

  async function handleSaveRename() {
    if (!canEdit || !batch) return;
    setNotice(null);
    const proposed = (renameDraft || '').trim();
    if (!proposed || proposed === batch.name) {
      setRenameErr(null);
      return;
    }
    const v = validateRealBatchRename({proposedName: proposed, currentName: batch.name, realBatches: allBatches});
    if (!v.ok) {
      setRenameErr(v.reason || 'invalid_name');
      return;
    }
    const saveKey = `cattle-batch-name:${batch.id}`;
    const saveAttempt = reportSaving(saveKey);
    try {
      // Migration 240: expected-before compare-and-set through the audited
      // operation; the server writes the 'Renamed X → Y' Activity row.
      await patchBatch.run({
        batchId: batch.id,
        operation: 'batch.update',
        target: {},
        expected: {name: batch.name},
        change: {name: proposed},
        requestId: newCattleRequestId(),
      });
      reportSaved(saveKey, saveAttempt);
    } catch (err) {
      reportSaveFailed(saveKey, () => void handleSaveRename(), saveAttempt);
      setRenameErr('db_error');
      setNotice({kind: 'error', message: 'Rename failed: ' + writeErrorMessage(err)});
      return;
    }
    setRenameErr(null);
    // The `await loadAll()` that stood here is gone, and it is not merely
    // relocated: `patchBatch.run` awaits the DECLARED invalidation set, which
    // contains this page's resource AND the seven others that read the row that
    // just changed. loadAll only ever refreshed this one.
  }

  async function handleDetach(cow: CattleRecord) {
    if (!canEdit || !batch || !cow || !cow.id) return;
    setNotice(null);
    // Atomic detach via SECDEF RPC (migration 081): the RPC reverts the herd,
    // writes the undo transfer audit row, clears the weigh-ins, AND logs the
    // field.updated Activity event in one transaction — so the client writes
    // no Activity here. Business-rule blocks come back as {ok:false, reason}.
    const r = await detachCattleFromProcessingBatch(sb, {
      cattleId: cow.id,
      batchId: batch.id,
      teamMember: authState && authState.name ? authState.name : null,
    });
    if (!r.ok) {
      const tag = cow.tag || r.tag || '?';
      if (r.reason === 'no_prior_herd') {
        setNotice({
          kind: 'warning',
          message: 'Cannot auto-detach #' + tag + ': no prior herd recorded. Manually move via the Herds tab.',
        });
      } else {
        setNotice({kind: 'error', message: 'Detach failed for #' + tag + ': ' + r.reason});
      }
      return;
    }
    invalidateCattleWeighInsCache();
    await loadAll();
  }

  async function handleUpdateScheduledDate() {
    if (!canEdit || !batch || batch.status !== 'scheduled') return;
    const nextDate = (scheduledDateDraft || '').trim();
    if (!nextDate || !/^\d{4}-\d{2}-\d{2}$/.test(nextDate)) return;
    if (nextDate === batch.planned_process_date) return;
    setNotice(null);
    const saveKey = `cattle-batch-scheduled-date:${batch.id}`;
    const saveAttempt = reportSaving(saveKey);
    try {
      // Migration 240: the operation enforces today's client gate (the date is
      // only editable while scheduled) and writes the
      // 'Scheduled date X → Y' Activity row.
      await patchBatch.run({
        batchId: batch.id,
        operation: 'batch.update',
        target: {},
        expected: {planned_process_date: batch.planned_process_date ?? null},
        change: {planned_process_date: nextDate},
        requestId: newCattleRequestId(),
      });
      reportSaved(saveKey, saveAttempt);
    } catch (err) {
      reportSaveFailed(saveKey, () => void handleUpdateScheduledDate(), saveAttempt);
      setNotice({kind: 'error', message: 'Date update failed: ' + writeErrorMessage(err)});
      return;
    }
    // The hand-written `Promise.all([loadAll(), forecastBundle.refetch()])` is
    // gone because the declared set is a strict superset of it, and both halves
    // are provably reached rather than assumed:
    //
    //   * cattle.batch.detail is this page's own resource — always mounted here;
    //   * cattle.forecast.bundle is mounted with `enabled: status ===
    //     'scheduled'`, and this function returns early unless the batch IS
    //     scheduled, so its observer is active exactly when this line runs.
    //
    // Invalidation refetches active observers, so both refetch as before; the
    // six other readers of this row are marked stale as well, which the pair
    // above never did.
  }

  async function handleUnschedule() {
    if (!canEdit || !batch || batch.status !== 'scheduled') return;
    setNotice(null);
    // Atomic unschedule via SECDEF RPC (migration 100): deletes the empty
    // scheduled batch, defensively unlinks any cattle, and logs record.deleted
    // in one transaction — replacing the unaudited direct client delete.
    const r = await unscheduleCattleProcessingBatch(sb, {
      batchId: batch.id,
      teamMember: authState && authState.name ? authState.name : null,
    });
    if (!r.ok) {
      setNotice({kind: 'error', message: 'Unschedule failed: ' + (r.reason === 'rpc_error' ? r.error : r.reason)});
      return;
    }
    navigate('/cattle/batches');
  }

  if (loading) {
    return <RecordPageLoading Header={Header} />;
  }

  if (loadError) {
    return (
      <RecordPageLoadError
        backLabel="Back to Processing Batches"
        onBack={() => navigate('/cattle/batches')}
        notice={loadError}
        onRetry={loadAll}
        maxWidth={900}
        data-cattle-batch-load-error="true"
      />
    );
  }

  if (!batch) {
    return (
      <RecordPageNotFound
        Header={Header}
        backLabel="Back to Processing Batches"
        onBack={() => navigate('/cattle/batches')}
        message="Batch not found."
      />
    );
  }

  const rows = cowsDetail();
  const {totalLive, totalHang, yieldPct} = computeRuminantBatchTotals(rows);
  const batchProcessDate = batch.actual_process_date || batch.planned_process_date;
  const isComplete = batch.status === 'complete';
  const isScheduled = batch.status === 'scheduled';
  const entityLabel = batch.name || batchId;
  const draftKey = (cid: string, field: string) => `${batch.id}|${cid}|${field}`;
  const draftVal = (cid: string, field: string, curr: any) => {
    const k = draftKey(cid, field);
    return cowDraft[k] != null ? cowDraft[k] : curr != null ? String(curr) : '';
  };

  return (
    <RecordPageFrame Header={Header}>
      <RecordPageBody maxWidth={900} data-cattle-batch-record-loaded="true">
        <RecordBackLink label="Back to Processing Batches" onBack={() => navigate('/cattle/batches')} />

        <RecordSequenceNav seq={recordSeq} currentId={batchId} onNavigate={navigateSeq} />

        <div style={{display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 12}}>
          <RecordTitle fontSize={22} margin={0}>
            {batch.name}
          </RecordTitle>
          <Badge status={processingStatusLabel(batch.status)} />
          {!isScheduled && (
            <span style={{fontSize: 12, color: 'var(--ink-muted)'}}>
              {rows.length} {rows.length === 1 ? 'cow' : 'cows'}
            </span>
          )}
          {/* A scheduled batch IS a booking (mig 208): black date, gray "Booked" (Q14-A). */}
          {isScheduled && batch.planned_process_date ? (
            <span style={{fontSize: 12}} data-cattle-batch-booked>
              <span style={{fontWeight: 700, color: 'var(--text-primary)'}}>{fmt(batch.planned_process_date)}</span>{' '}
              <span style={{color: 'var(--ink-muted)'}}>Booked</span>
            </span>
          ) : batch.actual_process_date || batch.planned_process_date ? (
            <span style={{fontSize: 12, color: 'var(--ink-muted)'}}>
              process date {fmt(batch.actual_process_date || batch.planned_process_date)}
            </span>
          ) : null}
          {yieldPct && (
            <span style={{fontSize: 12, fontWeight: 600, color: 'var(--text-primary)'}}>{yieldPct + '% yield'}</span>
          )}
          {/* Deep link into the Processing Calendar record for this batch —
              only meaningful once a planned or actual process date exists. */}
          {(batch.actual_process_date || batch.planned_process_date) && (
            <>
              <Button
                variant="secondary"
                type="button"
                data-processing-source-link="cattle"
                onClick={() => navigateToProcessingRoute(navigate, processingSourceRoute('cattle', batch.id))}
              >
                View in Processing →
              </Button>
            </>
          )}
        </div>

        {notice && <InlineNotice notice={notice} onDismiss={() => setNotice(null)} />}

        {/* Scheduled batch controls */}
        {isScheduled && canEdit && (
          <div
            style={{
              background: 'var(--surface)',
              border: '1px solid var(--border)',
              borderRadius: 'var(--r-card)',
              boxShadow: 'var(--shadow-card)',
              padding: '14px 18px',
              marginBottom: 12,
            }}
          >
            <div style={{display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 10}}>
              {/* Inline editor row: shared control/label styling (CP2); kept
                  inline next to the Unschedule action rather than a full grid
                  row. Save (handleUpdateScheduledDate onBlur) unchanged. */}
              <span style={recordFieldLabel}>Processor date</span>
              <input
                type="date"
                value={scheduledDateDraft}
                onChange={(e) => setScheduledDateDraft(e.target.value)}
                onBlur={handleUpdateScheduledDate}
                data-scheduled-batch-date={batch.id}
                style={{...recordControl, width: 180}}
              />
            </div>
            {!unscheduling ? (
              <Button
                variant="secondary"
                type="button"
                onClick={() => setUnscheduling(true)}
                data-scheduled-batch-unschedule={batch.id}
              >
                Unschedule
              </Button>
            ) : (
              <div
                data-scheduled-batch-unschedule-warning={batch.id}
                style={{display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap'}}
              >
                <span style={{fontSize: 12, color: 'var(--danger-ink)'}}>Remove this planned batch?</span>
                <Button
                  variant="secondary"
                  type="button"
                  onClick={() => setUnscheduling(false)}
                  data-scheduled-batch-unschedule-cancel={batch.id}
                >
                  Cancel
                </Button>
                <Button
                  variant="secondary"
                  type="button"
                  onClick={handleUnschedule}
                  data-scheduled-batch-unschedule-confirm={batch.id}
                >
                  Confirm unschedule
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Projected roster — scheduled batches only. cows_detail stays empty
            until Send-to-Processor, so the cohort renders as the LIVE
            forecast projection (canonical adapter), clearly labeled
            Projected. It is replaced by the actual attached cattle the
            moment the batch goes active — projections never mix into the
            actual roster or its weights. */}
        {isScheduled && (
          <div
            data-scheduled-projected-roster={batch.id}
            style={{
              background: 'var(--surface)',
              border: '1px solid var(--border)',
              borderRadius: 'var(--r-card)',
              boxShadow: 'var(--shadow-card)',
              padding: '14px 18px',
              marginBottom: 12,
            }}
          >
            <div style={{display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4}}>
              <span style={{fontSize: 13, fontWeight: 700, color: 'var(--ink)'}}>Projected roster</span>
              <Badge variant="neutral">Projected</Badge>
            </div>
            <div style={{fontSize: 11, color: 'var(--ink-muted)', marginBottom: 10}}>
              Live forecast for {batch.planned_process_date ? fmt(batch.planned_process_date) : 'the planned month'} —
              updates with new weigh-ins until cattle are actually sent from WeighIns. Nothing is attached to this
              record yet.
            </div>
            {!projectedRoster || projectedRoster.state === 'loading' ? (
              <div style={{fontSize: 12, color: 'var(--ink-faint)'}}>Computing projection{'…'}</div>
            ) : projectedRoster.state === 'unavailable' ? (
              <div data-projected-roster-unavailable="1" style={{fontSize: 12, color: 'var(--danger-ink)'}}>
                Projected roster unavailable — the forecast inputs could not be loaded. Refresh to retry.
              </div>
            ) : (
              <ProjectedRosterTable roster={projectedRoster.roster} processDate={batch.planned_process_date} />
            )}
          </div>
        )}

        {/* Active/complete batch workspace */}
        {!isScheduled && (
          <div
            style={{
              background: 'var(--surface)',
              border: '1px solid var(--border)',
              borderRadius: 'var(--r-card)',
              boxShadow: 'var(--shadow-card)',
              padding: '14px 18px',
              marginBottom: 12,
            }}
          >
            {canEdit && (
              <div style={{display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 10}}>
                {!isComplete && (
                  <>
                    {/* Inline rename editor: shared control/label styling (CP2);
                        kept inline next to the Complete/Reopen action. Save
                        (handleSaveRename onBlur + validation) unchanged. */}
                    <span style={recordFieldLabel}>Name</span>
                    <input
                      type="text"
                      value={renameDraft}
                      onChange={(e) => {
                        setRenameDraft(e.target.value);
                        setRenameErr(null);
                      }}
                      onBlur={handleSaveRename}
                      data-rename-input={batch.id}
                      style={{...recordControl, width: 160}}
                    />
                    {renameErr && (
                      <span style={{fontSize: 11, color: 'var(--danger-ink)'}} data-rename-err={batch.id}>
                        {renameErr === 'format' && 'Use C-YY-NN'}
                        {renameErr === 'duplicate' && 'Name already used'}
                        {renameErr === 'sequence_gap' && 'Would skip a sequence number'}
                        {renameErr === 'new_year_must_start_at_01' && 'New year must start at 01'}
                        {renameErr === 'db_error' && 'Save error'}
                      </span>
                    )}
                  </>
                )}
                <span style={{flex: 1}} />
                {!isComplete ? (
                  <Button variant="primary" onClick={handleMarkComplete} data-mark-complete={batch.id}>
                    Mark complete
                  </Button>
                ) : (
                  <Button variant="secondary" onClick={handleReopen} data-reopen={batch.id}>
                    Reopen to In Process
                  </Button>
                )}
              </div>
            )}

            <RuminantBatchStatsGrid>
              <RuminantBatchStat
                label="Live wt total"
                value={totalLive > 0 ? Math.round(totalLive).toLocaleString() + ' lb' : '—'}
              />
              <RuminantBatchStat
                label="Hanging wt"
                value={totalHang > 0 ? Math.round(totalHang).toLocaleString() + ' lb' : '—'}
              />
              <RuminantBatchStat
                label="Yield"
                value={yieldPct ? yieldPct + '%' : '—'}
                color={yieldPct ? 'var(--text-primary)' : 'var(--ink-faint)'}
              />
              <RuminantBatchStat
                label="Cost"
                value={batch.processing_cost ? '$' + batch.processing_cost.toLocaleString() : '—'}
              />
            </RuminantBatchStatsGrid>

            {rows.length > 0 && (
              <div style={{display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 8}}>
                {rows.map((r) => {
                  const cow = cattle.find((c) => c.id === r.cattle_id);
                  const lv = parseFloat(r.live_weight);
                  const hw = parseFloat(r.hanging_weight);
                  const y = lv > 0 && hw > 0 ? Math.round((hw / lv) * 1000) / 10 : null;
                  const processingAge = ageAtProcessing(cow?.birth_date, batchProcessDate);
                  const weightDisabled = !canEdit || isComplete;
                  const weightStyle: React.CSSProperties = {
                    fontSize: 13,
                    padding: '6px 8px',
                    border: '1px solid var(--border)',
                    borderRadius: 'var(--r-control)',
                    fontFamily: 'inherit',
                    width: '100%',
                    minWidth: 70,
                    boxSizing: 'border-box',
                    background: weightDisabled ? 'var(--surface-2)' : 'var(--surface)',
                    color: 'var(--ink)',
                    opacity: 1,
                    // iOS disabled-input legibility fix (infrastructure value,
                    // like getReadableText) — locked by cattle_batch_record_page_static.
                    WebkitTextFillColor: '#1A1F24',
                  };
                  return (
                    <div key={r.cattle_id} data-batch-cow-row={r.cattle_id} style={ruminantBatchRowCardStyle}>
                      <div style={{display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4}}>
                        <span style={{fontWeight: 700, color: 'var(--ink)', minWidth: 50}}>
                          {'#' + (r.tag || cow?.tag || '?')}
                        </span>
                        <span style={{fontSize: 11, color: 'var(--ink-muted)'}}>{cow?.breed || '—'}</span>
                        <span
                          data-batch-cow-processing-age={r.cattle_id}
                          style={{fontSize: 11, color: 'var(--ink-muted)', fontVariantNumeric: 'tabular-nums'}}
                        >
                          {'Age at processing ' + processingAge}
                        </span>
                        {y && (
                          <span style={{fontSize: 11, fontWeight: 600, color: 'var(--text-primary)'}}>
                            {y + '% yield'}
                          </span>
                        )}
                        <span style={{flex: 1}} />
                        {canEdit && !isComplete && (
                          <button
                            onClick={() => handleDetach(cow || {id: r.cattle_id, tag: r.tag})}
                            title="Detach cow from batch (reverts herd)"
                            style={ruminantBatchDetachButtonStyle}
                          >
                            ×
                          </button>
                        )}
                      </div>
                      <div style={{display: 'flex', gap: 8, flexWrap: 'wrap'}}>
                        <label style={{flex: '1 1 120px', minWidth: 0}}>
                          <div style={ruminantBatchWeightLabelStyle}>Live wt (lb)</div>
                          <input
                            type="number"
                            min="0"
                            step="0.1"
                            placeholder="—"
                            value={draftVal(r.cattle_id, 'live', r.live_weight)}
                            disabled={weightDisabled}
                            data-batch-live-weight={r.cattle_id}
                            onChange={(e) =>
                              setCowDraft((p) => ({...p, [draftKey(r.cattle_id, 'live')]: e.target.value}))
                            }
                            onBlur={(e) => {
                              saveCowWeight(r.cattle_id, 'live_weight', e.target.value);
                              setCowDraft((p) => {
                                const x = {...p};
                                delete x[draftKey(r.cattle_id, 'live')];
                                return x;
                              });
                            }}
                            style={weightStyle}
                          />
                        </label>
                        <label style={{flex: '1 1 120px', minWidth: 0}}>
                          <div style={ruminantBatchWeightLabelStyle}>Hanging wt (lb)</div>
                          <input
                            type="number"
                            min="0"
                            step="0.1"
                            placeholder="—"
                            value={draftVal(r.cattle_id, 'hanging', r.hanging_weight)}
                            disabled={weightDisabled}
                            data-batch-hanging-weight={r.cattle_id}
                            onChange={(e) =>
                              setCowDraft((p) => ({...p, [draftKey(r.cattle_id, 'hanging')]: e.target.value}))
                            }
                            onBlur={(e) => {
                              saveCowWeight(r.cattle_id, 'hanging_weight', e.target.value);
                              setCowDraft((p) => {
                                const x = {...p};
                                delete x[draftKey(r.cattle_id, 'hanging')];
                                return x;
                              });
                            }}
                            style={weightStyle}
                          />
                        </label>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            {rows.length === 0 && !isScheduled && (
              <div style={{fontSize: 12, color: 'var(--ink-faint)', fontStyle: 'italic'}}>No cows attached yet.</div>
            )}
            {batch.notes && (
              <div style={{marginTop: 6, fontSize: 11, color: 'var(--ink-muted)', fontStyle: 'italic'}}>
                {batch.notes}
              </div>
            )}
          </div>
        )}

        <RecordCollaborationSection
          sb={sb}
          authState={authState}
          entityType="cattle.processing"
          entityId={batch.id}
          entityLabel={entityLabel}
        />
      </RecordPageBody>
    </RecordPageFrame>
  );
}
